/**
 * ─────────────────────────────────────────────
 * src/lib/api.js
 * ─────────────────────────────────────────────
 *
 * WHY THIS FILE EXISTS:
 * Instead of writing fetch('/api/auth/login', { headers: {...} }) in every
 * component, we create ONE axios "instance" with shared config.
 * Every API call in the app uses this instance.
 *
 * WHAT IS AN AXIOS INSTANCE?
 * Think of it as a pre-configured HTTP client.
 * Like a template: baseURL is already set, headers are already set.
 * You just call api.post('/auth/login', data) and it handles the rest.
 *
 * WHAT IS AN INTERCEPTOR?
 * A function that runs BEFORE every request or AFTER every response.
 * - Request interceptor: "Before you send this request, add the token"
 * - Response interceptor: "If you get a 401, log the user out"
 */

import axios from "axios";

// ─────────────────────────────────────────────
// Create the axios instance
// baseURL: all requests will be prefixed with this
// So api.post('/auth/login') → POST http://localhost:3000/api/auth/login
// ─────────────────────────────────────────────
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:3000/api",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000, // 10 seconds — don't hang forever
});

// ─────────────────────────────────────────────
// REQUEST INTERCEPTOR
// Runs before EVERY request this axios instance makes.
//
// WHY: Some routes need Authorization header (protected routes).
//      Instead of adding the token manually in every API call,
//      this interceptor automatically reads from localStorage and adds it.
//
// Example without interceptor (bad — repeated everywhere):
//   axios.get('/dashboard', { headers: { Authorization: `Bearer ${token}` } })
//
// Example with interceptor (good — done once here):
//   api.get('/dashboard')  ← token is added automatically
// ─────────────────────────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      // Attach token to every request if it exists
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config; // Must return config — axios needs it to proceed
  },
  (error) => {
    // If there's an error BUILDING the request (rare), reject it
    return Promise.reject(error);
  },
);

// ─────────────────────────────────────────────
// RESPONSE INTERCEPTOR
// Runs after EVERY response comes back.
//
// WHY: Handle global errors in one place.
//      If the server returns 401 (Unauthorized / token expired),
//      we automatically clear localStorage and redirect to login.
//      This prevents a user with an expired token from being "stuck".
// ─────────────────────────────────────────────
api.interceptors.response.use(
  (response) => {
    // Success (2xx status): just pass the response through unchanged
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const isOnAuthPage = [
        "/",
        "/forgot-password",
        "/otp",
        "/reset-password",
        "/set-password",
      ].includes(window.location.pathname);

      if (!isOnAuthPage) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/";
      }
    }

    return Promise.reject(error);
  },
);

export default api;

// ─────────────────────────────────────────────
// Keep legacy helpers so existing imports don't break
// ─────────────────────────────────────────────

// Callers pass paths like "/api/leaves/apply". Relative URLs only work in
// dev via the CRA proxy — in a production build they hit the frontend's own
// origin and 404. Resolve against the configured backend origin instead
// (REACT_APP_API_URL minus its trailing /api, since callers include /api).
const API_ORIGIN = (
  process.env.REACT_APP_API_URL || "http://localhost:3000/api"
).replace(/\/api\/?$/, "");

const resolveApiUrl = (url) =>
  url.startsWith("http") ? url : `${API_ORIGIN}${url}`;

// Mirror the axios 401 policy: expired/invalid token logs the user out
// instead of leaving pages silently empty.
const handleAuthExpiry = (res) => {
  if (res.status === 401) {
    const isOnAuthPage = [
      "/",
      "/forgot-password",
      "/otp",
      "/reset-password",
      "/set-password",
    ].includes(window.location.pathname);
    if (!isOnAuthPage) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/";
    }
  }
  return res;
};

export const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("token");
  return fetch(resolveApiUrl(url), {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  }).then(handleAuthExpiry);
};

export const authFetchFormData = (url, options = {}) => {
  const token = localStorage.getItem("token");
  return fetch(resolveApiUrl(url), {
    ...options,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  }).then(handleAuthExpiry);
};

export const safeJsonParse = (str, fallback = null) => {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};
